import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import {
  Table,
  Button,
  Select,
  Space,
  message,
  Alert,
  Tag,
  Input,
  Checkbox,
  Pagination,
} from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";

/**
 * Manual backfill form: assign tblAdminUser to older tblPackagePurchase rows
 * (UserID + PaymentReceivedBy). New subscriptions set this automatically on add.
 */
function ManagePaymentReceivedBy() {
  const UserID = localStorage.getItem("AdminID");
  const AccessKey = localStorage.getItem("AdminAccessKey");

  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  const [pumpCode, setPumpCode] = useState("");
  const [PageNo, setPageNo] = useState(1);
  const [PageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  const loadAdmins = () => {
    axios({
      method: "post",
      url: config.base_url + "AdminUser/GetAdminUsersForAssign",
      headers: { "Content-Type": "application/json" },
      data: { UserID, AccessKey },
    })
      .then((response) => {
        if (response.data.status_code == 1) {
          setAdminUsers(response.data.ListofRecords || []);
        } else {
          message.error(response.data.status_message || "Unable to load admins.");
        }
      })
      .catch(() => message.error("Network error loading admin users."));
  };

  const loadPurchases = () => {
    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "AdminUser/GetAllPurchases",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        PageNo,
        PageSize,
        PumpCode: pumpCode || "",
        UnassignedReceivedByOnly: unassignedOnly,
        HideZeroBalance: false,
      },
    })
      .then((response) => {
        if (response.data.status_code == 1) {
          const list = response.data.listofRecords || [];
          setRows(list);
          setTotalRecords(response.data.totalRecords || 0);
          const next = {};
          list.forEach((r) => {
            if (r.UserID > 0) next[r.ID] = r.UserID;
          });
          setAssignments(next);
        } else {
          message.error(response.data.status_message || "Unable to load purchases.");
          setRows([]);
          setTotalRecords(0);
        }
      })
      .catch(() => message.error("Network error loading purchases."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Assign Payment Received By";
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PageNo, PageSize, unassignedOnly]);

  const handleSave = (record) => {
    const isTrial =
      (record.PaymentMode || "").toLowerCase() === "trial";
    const assignedUserID = isTrial ? 1 : assignments[record.ID];

    if (!isTrial && (!assignedUserID || assignedUserID <= 0)) {
      message.warning("Select an admin user before saving.");
      return;
    }

    setSavingId(record.ID);
    axios({
      method: "post",
      url: config.base_url + "AdminUser/UpdatePackagePurchaseReceivedBy",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        ID: record.ID,
        AssignedUserID: assignedUserID,
      },
    })
      .then((response) => {
        if (response.data.status_code == 1) {
          message.success(response.data.status_message);
          loadPurchases();
        } else {
          message.error(response.data.status_message || "Update failed.");
        }
      })
      .catch(() => message.error("Network error updating record."))
      .finally(() => setSavingId(null));
  };

  const adminOptions = adminUsers.map((u) => ({
    value: u.ID,
    label: `${u.Full_Name || "Admin"} (#${u.ID})`,
  }));

  const columns = [
    {
      title: "ID",
      dataIndex: "ID",
      key: "ID",
      width: 70,
    },
    {
      title: "Pump",
      key: "pump",
      render: (_, r) => (
        <span>
          <strong>{r.PumpCode}</strong>
          <br />
          <span style={{ color: "#64748b", fontSize: 12 }}>{r.Name}</span>
        </span>
      ),
    },
    {
      title: "Package",
      dataIndex: "PackageName",
      key: "PackageName",
    },
    {
      title: "Mode",
      dataIndex: "PaymentMode",
      key: "PaymentMode",
      render: (v) =>
        (v || "").toLowerCase() === "trial" ? (
          <Tag color="orange">Trial</Tag>
        ) : (
          <Tag>{v || "-"}</Tag>
        ),
    },
    {
      title: "Start / End",
      key: "dates",
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {dayjs(r.StartDate).format(config.date_format)} →{" "}
          {dayjs(r.EndDate).format(config.date_format)}
        </span>
      ),
    },
    {
      title: "Current Received By",
      key: "current",
      render: (_, r) => (
        <span>
          {r.PaymentReceivedBy || "-"}
          <br />
          <Tag color={r.UserID > 0 ? "green" : "red"}>
            UserID: {r.UserID > 0 ? r.UserID : "unassigned"}
          </Tag>
        </span>
      ),
    },
    {
      title: "Assign Admin",
      key: "assign",
      width: 260,
      render: (_, r) => {
        const isTrial = (r.PaymentMode || "").toLowerCase() === "trial";
        if (isTrial) {
          return (
            <span style={{ fontSize: 12, color: "#64748b" }}>
              Locked to Admin UserID = 1 (Trial)
            </span>
          );
        }
        return (
          <Select
            style={{ width: "100%" }}
            placeholder="Select admin"
            options={adminOptions}
            value={assignments[r.ID] || undefined}
            onChange={(val) =>
              setAssignments((prev) => ({ ...prev, [r.ID]: val }))
            }
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      render: (_, r) => (
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          loading={savingId === r.ID}
          onClick={() => handleSave(r)}
        >
          Save
        </Button>
      ),
    },
  ];

  return (
    <div className="container-fluid">
      <div className="page-content">
        <div className="page-header mb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <NavLink to="/controlbase/petrolpump/subscriptions/">
                <Button icon={<ChevronLeftIcon width={16} />} />
              </NavLink>
              <h4 className="mb-0">Assign Payment Received By</h4>
            </div>
            <Button icon={<ReloadOutlined />} onClick={loadPurchases}>
              Refresh
            </Button>
          </div>
        </div>

        <Alert
          className="mb-3"
          type="info"
          showIcon
          message="Backfill historical subscription rows"
          description="New add-subscription saves automatically use the logged-in admin UserID and name. Use this page to correct older records. Trial rows are always set to admin UserID = 1."
        />

        <Space wrap className="mb-3">
          <Input
            placeholder="Filter pump code"
            value={pumpCode}
            onChange={(e) => setPumpCode(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
          <Checkbox
            checked={unassignedOnly}
            onChange={(e) => {
              setPageNo(1);
              setUnassignedOnly(e.target.checked);
            }}
          >
            Unassigned only (UserID = 0)
          </Checkbox>
          <Button
            type="primary"
            onClick={() => {
              setPageNo(1);
              loadPurchases();
            }}
          >
            Search
          </Button>
        </Space>

        <Table
          size="small"
          rowKey="ID"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: "max-content" }}
          bordered
        />

        <div className="text-end mt-3">
          <Pagination
            current={PageNo}
            pageSize={PageSize}
            size="small"
            total={totalRecords}
            onChange={(page) => setPageNo(page)}
            onShowSizeChange={(current, size) => {
              setPageNo(1);
              setPageSize(size);
            }}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `Total ${total} items`}
          />
        </div>
      </div>
    </div>
  );
}

export default ManagePaymentReceivedBy;
